import { Plus, ArrowRight, Download, Trash2, Check } from 'lucide-react';
import { Button } from '@components/base/actions';
import { Icon, Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const variants = ['primary', 'secondary', 'ghost', 'danger', 'success'] as const;
const sizes = ['sm', 'md', 'lg'] as const;

function ButtonShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="button">
        <Button
          variant={playgroundProps.variant as 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'}
          size={playgroundProps.size as 'sm' | 'md' | 'lg'}
          disabled={playgroundProps.disabled as boolean}
          loading={playgroundProps.loading as boolean}
        >
          Submit Claim
        </Button>
      </ShowcasePreviewSection>

      {/* Size Variants */}
      <ShowcaseDemoSection
        title="Size Variants"
        description="Buttons come in three sizes: small (32px), medium (40px), and large (48px)"
       
      >
        <HStack gap={4} align="center">
          {sizes.map((size) => (
            <Button key={size} size={size}>
              {size.toUpperCase()} Button
            </Button>
          ))}
        </HStack>
      </ShowcaseDemoSection>

      {/* Style Variants */}
      <ShowcaseDemoSection
        title="Style Variants"
        description="Five visual variants for different use cases"
       
      >
        <HStack gap={4} wrap="wrap">
          {variants.map((variant) => (
            <Button key={variant} variant={variant}>
              {variant.charAt(0).toUpperCase() + variant.slice(1)}
            </Button>
          ))}
        </HStack>
      </ShowcaseDemoSection>

      {/* Loading States */}
      <ShowcaseDemoSection
        title="Loading States"
        description="Buttons display a spinner when in loading state"
       
      >
        <HStack gap={4} wrap="wrap">
          {variants.map((variant) => (
            <Button key={variant} variant={variant} loading>
              Processing
            </Button>
          ))}
        </HStack>
      </ShowcaseDemoSection>

      {/* Disabled States */}
      <ShowcaseDemoSection
        title="Disabled States"
        description="All variants in their disabled state"
       
      >
        <HStack gap={4} wrap="wrap">
          {variants.map((variant) => (
            <Button key={variant} variant={variant} disabled>
              {variant.charAt(0).toUpperCase() + variant.slice(1)}
            </Button>
          ))}
        </HStack>
      </ShowcaseDemoSection>

      {/* With Icons */}
      <ShowcaseDemoSection
        title="With Icons"
        description="Buttons can include icons on the left or right side"
       
      >
        <VStack gap={4}>
          <Text size="sm" color="secondary">Left Icons</Text>
          <HStack gap={4} wrap="wrap">
            <Button leftIcon={<Icon icon={Plus} size="sm" />}>
              Add Policy
            </Button>
            <Button variant="secondary" leftIcon={<Icon icon={Download} size="sm" />}>
              Download Report
            </Button>
            <Button variant="success" leftIcon={<Icon icon={Check} size="sm" />}>
              Approve Claim
            </Button>
          </HStack>

          <Text size="sm" color="secondary" className="mt-4">Right Icons</Text>
          <HStack gap={4} wrap="wrap">
            <Button rightIcon={<Icon icon={ArrowRight} size="sm" />}>
              Continue
            </Button>
            <Button variant="danger" rightIcon={<Icon icon={Trash2} size="sm" />}>
              Delete
            </Button>
          </HStack>
        </VStack>
      </ShowcaseDemoSection>

      {/* Icon-Only Buttons */}
      <ShowcaseDemoSection
        title="Icon-Only Buttons"
        description="Square buttons with just an icon (require aria-label for accessibility)"
       
      >
        <HStack gap={4}>
          <Button leftIcon={<Icon icon={Plus} size="sm" />} aria-label="Add new item" />
          <Button variant="secondary" leftIcon={<Icon icon={Download} size="sm" />} aria-label="Download" />
          <Button variant="ghost" leftIcon={<Icon icon={ArrowRight} size="sm" />} aria-label="Next" />
          <Button variant="danger" leftIcon={<Icon icon={Trash2} size="sm" />} aria-label="Delete" />
          <Button variant="success" leftIcon={<Icon icon={Check} size="sm" />} aria-label="Approve" />
        </HStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const ButtonShowcase: ShowcaseDefinition = {
  id: 'button',
  name: 'Button',
  category: 'actions',
  description: 'Interactive button component for triggering actions',
  propsDefinition: [
    {
      name: 'variant',
      type: 'select',
      options: ['primary', 'secondary', 'ghost', 'danger', 'success'],
      defaultValue: 'primary',
      description: 'Visual style variant of the button',
    },
    {
      name: 'size',
      type: 'select',
      options: ['sm', 'md', 'lg'],
      defaultValue: 'md',
      description: 'Size of the button (sm=32px, md=40px, lg=48px)',
    },
    {
      name: 'disabled',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the button is disabled',
    },
    {
      name: 'loading',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the button shows a loading spinner',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="button" (implicit)',
      'aria-disabled (when disabled)',
      'aria-busy (when loading)',
    ],
    keyboardNav: [
      'Enter/Space: Activate button',
      'Tab: Move focus to/from button',
    ],
    notes: [
      'Provides visible focus indicator (2px ring)',
      'Loading state prevents interaction',
      'Icon-only buttons require aria-label',
    ],
  },
  component: ButtonShowcaseComponent,
};
